import {
  getItemOrDefaultToValue,
  getPersonName,
  getPhoneNumber,
  nestedDisplay,
} from '../../details';
import { images } from 'utils/icons';
import {
  siteManageVisitorKey,
  siteRequiresPassKey,
  siteRequiresPhotoIdKey,
  siteRequiresPhotoKey,
  siteRequiresPhoneKey,
  visitorPurposes,
} from 'utils/constants/visitor';
import { columnView } from 'containers/Pages/Locations/Visitors/Layouts/options';
import {
  getSitePolicyPassType,
  getSitePolicyPassTypeId,
  getSitePolicyPhotoIdType,
  getSitePolicyPhotoIdTypeId,
  getSitePolicyPhotoType,
  getSitePolicyPhotoTypeId,
} from 'utils/helper/site';
import COLORS from 'utils/constants/colors';
import get from 'lodash/get';
import { hasService } from 'utils/helper/product';
import { serviceTypes } from 'utils/constants/serviceTypes';
import {
  siteAssetColumns,
  siteUserColumns,
} from 'containers/Pages/Locations/Sites/columns';
import { getAssetCategoryData } from 'utils/helper/assetType';
import { isEmpty } from 'lodash';
import {
  transformFormPurposes,
  transformPurposes,
} from 'utils/DataTransform/Sites/SiteForm';

export const mapMediaTypeKey = values => {
  return values.map(val => ({ ...val, mediaType: 'IMAGE' }));
};

export const transformAsset = assignedAssets => {
  if (isEmpty(assignedAssets)) return [];
  return assignedAssets.map(item => ({
    id: item.id,
    category: getAssetCategoryData(item.type.category),
    assetTypeId: getItemOrDefaultToValue(item.type, 'assetTypeId', '-'),
    assetTypeName: getItemOrDefaultToValue(item.type, 'name', '-'),
    assetId: getItemOrDefaultToValue(item, 'assetId', '-'),
    name: getItemOrDefaultToValue(item, 'name', '-'),
  }));
};
export const transformSiteUser = assignedUsers => {
  if (isEmpty(assignedUsers)) return [];
  return assignedUsers.map(item => ({
    id: item.id,
    name: `${item.firstName} ${item.lastName}`,
    phone:
      item.phoneNumber !== null
        ? `${getItemOrDefaultToValue(
            item.phoneNumber,
            'prefix',
          )} ${getItemOrDefaultToValue(item.phoneNumber, 'number', '-')}`
        : '-',
    email: getItemOrDefaultToValue(item, 'email', '-'),
  }));
};
export const transform = ({
  data,
  visitors,
  globalDepartmentList = [],
  services = [],
}) => {
  const manageVisitor = getItemOrDefaultToValue(
    data.visitorPolicy,
    'managesVisitors',
    false,
  );
  // This gives the list of assigned departments for this customer
  const selectedDepartments = globalDepartmentList.filter(f =>
    getItemOrDefaultToValue(data, 'departmentIds', []).includes(f.id),
  );

  // This will give the assigned departments for the linked businessClient in customers object
  const assignedDepartmentsForBusinessClient =
    data.businessClient.departmentIds;

  const bizClient = {
    id: get(data, 'businessClient.id', ''),
    name: get(data, 'businessClient.businessClientName', ''),
    departmentIds: assignedDepartmentsForBusinessClient,
  };
  const isVisitorManagementEnabled = hasService({
    services,
    service: serviceTypes.visitorManagement,
  });

  const customerDisplayName = getPersonName(
    getItemOrDefaultToValue(data.contact, 'general', '-'),
  );
  return {
    generalInfo: {
      siteId: {
        label: 'Site ID',
        type: 'text',
        value: getItemOrDefaultToValue(data, 'siteId', '-'),
      },
      name: {
        type: 'text',
        value: getItemOrDefaultToValue(data, 'name', '-'),
      },
      departments: {
        type: 'array',
        label: 'Departments',
        value: nestedDisplay(selectedDepartments),
      },
    },
    detailedInfo: {
      businessClient: {
        type: 'text',
        value: getItemOrDefaultToValue(
          data.businessClient,
          'businessClientName',
          false,
        ),
      },
      contact: {
        type: 'text',
        value:
          data.contact != null
            ? getPersonName(
                getItemOrDefaultToValue(data.contact, 'general', '-'),
              )
            : '-',
      },
      contactNumber: {
        type: 'text',
        value:
          data.contact != null
            ? getPhoneNumber(
                getItemOrDefaultToValue(
                  data.contact.general,
                  'phoneNumber',
                  '-',
                ),
              )
            : '-',
      },
      [siteManageVisitorKey]: {
        type: 'boolean',
        value: manageVisitor,
      },
      surfaces: {
        type: 'detailImageTable',
        label: 'Images',
        value: mapMediaTypeKey(getItemOrDefaultToValue(data, 'surfaces', [])),
      },
      ...(manageVisitor && {
        [siteRequiresPassKey]: {
          type: 'text',
          value: getSitePolicyPassType(data.visitorPolicy),
        },
        [siteRequiresPhotoIdKey]: {
          type: 'text',
          value: getSitePolicyPhotoIdType(data.visitorPolicy),
        },
        [siteRequiresPhotoKey]: {
          type: 'text',
          value: getSitePolicyPhotoType(data.visitorPolicy),
        },
        [siteRequiresPhoneKey]: {
          type: 'boolean',
          value: getItemOrDefaultToValue(
            data.visitorPolicy,
            'requiresPhone',
            false,
          ),
        },
        [visitorPurposes]: {
          type: 'array',
          value: transformPurposes(data.visitorPolicy.purposes),
        },
      }),
      AssignedPeople: {
        type: 'list-data',
        value: {
          data: transformSiteUser(data.assignedUsers),
          columns: siteUserColumns,
        },
      },
      ...(isVisitorManagementEnabled && {
        visitors: {
          type: 'list-data',
          value: {
            data: visitors,
            columns: columnView('managerial'),
          },
        },
      }),
      AssignedAssets: {
        type: 'list-data',
        value: {
          data: transformAsset(data.assignedAssets),
          columns: siteAssetColumns,
        },
      },
      locationDetail: {
        type: 'map-array',
        label: 'Location',
        value: {
          id: getItemOrDefaultToValue(data, 'id'),
          riskLevel: 'SITE',
          address: getItemOrDefaultToValue(data, 'address', '-'),
          addressLocationMarker: {
            icon: 'addressMarker',
            lat: data.boundary.centreOfGravity.latitude,
            lng: data.boundary.centreOfGravity.longitude,
          },
          center: {
            lat: data.boundary.centreOfGravity.latitude,
            lng: data.boundary.centreOfGravity.longitude,
          },
          boundary: getItemOrDefaultToValue(data.boundary, 'points', []).map(
            boundary => {
              return {
                lat: boundary.latitude,
                lng: boundary.longitude,
                radius: boundary.radius,
                isWaypoint: boundary.isWaypoint,
              };
            },
          ),
          tableView: false,
        },
      },
    },
    others: {
      id: {
        value: getItemOrDefaultToValue(data, 'id'),
      },
      businessClient: {
        value: bizClient,
      },
      image: {
        value:
          data?.surfaces && data.surfaces.length > 0
            ? data.surfaces[0].imageUrl
            : images.iconSite.default,
      },
      archived: {
        value: getItemOrDefaultToValue(data, 'archived', false),
      },
      status: {
        value: getItemOrDefaultToValue(data, 'status', 'INACTIVE'),
      },
      timezone: {
        value: getItemOrDefaultToValue(data, 'timezone'),
      },
      contact: {
        value:
          data.contact != null
            ? {
                id: data.contact.general.id,
                value: {
                  general: {
                    ...data.contact.general,
                    businessClientId: data.businessClient.id, // business client info is required for linking it with contact
                    businessClientName: data.businessClient.name,
                  },
                },
                label: customerDisplayName,
              }
            : '',
      },
      address: {
        value: getItemOrDefaultToValue(data, 'address'),
      },
      locations: {
        value:
          data.boundary != null
            ? [
                {
                  editable: true,
                  draggable: false,
                  color: COLORS.DARK_GREY_02,
                  isFilled: true,
                  paths: getItemOrDefaultToValue(data.boundary, 'points').map(
                    point => ({
                      lat: point.latitude,
                      lng: point.longitude,
                    }),
                  ),
                },
              ]
            : [],
      },
      patrols: {
        value: getItemOrDefaultToValue(data, 'patrols'),
      },
      [siteRequiresPassKey]: {
        type: 'text',
        value: getSitePolicyPassTypeId(data.visitorPolicy),
      },
      [siteRequiresPhotoIdKey]: {
        type: 'text',
        value: getSitePolicyPhotoIdTypeId(data.visitorPolicy),
      },
      [siteRequiresPhotoKey]: {
        type: 'text',
        value: getSitePolicyPhotoTypeId(data.visitorPolicy),
      },
      [visitorPurposes]: {
        value: transformFormPurposes(data.visitorPolicy.purposes),
      },
      centreOfGravity: getItemOrDefaultToValue(
        data.boundary,
        'centreOfGravity',
        null,
      ),
    },
  };
};
